import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { AGE_BOUND_UNITS, type AgeBoundUnit } from '@wodi/domain';
import type {
  KhisMappingRowInputView,
  KhisMappingVersionView,
  KhisMappingView,
} from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  EmptyState,
  LoadingBlock,
  Notice,
  Row,
  Section,
  Select,
  Table,
  TableBody,
  TableHeader,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import {
  activateKhisDraft,
  createKhisDraft,
  khisKeys,
  khisMappingQuery,
  saveKhisDraft,
  setKhisOrgUnit,
} from '../api/khis.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { screen, TableScroll } from '../layout/screen.tsx';
import { SettingsPage } from '../routes/settings-nav.tsx';
import { toasts } from '../toasts.ts';
import { errorText, LoadFailed, whenWords } from './format.tsx';
import styles from './khis.module.css';

// Settings > KHIS (docs/plans/phase3-khis-and-quality.md §4): this facility's KHIS
// organisation unit, and the mapping from what Wodi counts to KHIS data elements and
// category option combos, as versioned data. The UIDs come from KHIS's own metadata; none
// is built into Wodi (TODO(verify-spec) until the KHIS data sets are supplied).

const UNIT_WORDS: Record<AgeBoundUnit, string> = { days: 'days', months: 'months', years: 'years' };
const EVERY_SEX = 'every';
const NO_LIMIT = 'none';

function ageWords(row: KhisMappingRowInputView): string {
  const b = (x: { unit: AgeBoundUnit; value: number }) =>
    `${String(x.value)} ${UNIT_WORDS[x.unit]}`;
  if (row.ageFrom === null && row.ageBelow === null) return 'Every age';
  if (row.ageFrom === null && row.ageBelow !== null) return `Under ${b(row.ageBelow)}`;
  if (row.ageFrom !== null && row.ageBelow === null) return `${b(row.ageFrom)} and over`;
  if (row.ageFrom !== null && row.ageBelow !== null)
    return `${b(row.ageFrom)} to under ${b(row.ageBelow)}`;
  return 'Every age';
}

function MappingTable({
  version,
  view,
}: {
  version: KhisMappingVersionView;
  view: KhisMappingView;
}) {
  const sourceTitle = (key: string) => view.sources.find((s) => s.key === key)?.title ?? key;
  const sexWords = (code: string | null) =>
    code === null ? 'Every sex' : (view.sexOptions.find((o) => o.code === code)?.display ?? code);
  if (version.rows.length === 0) return <EmptyState>No rows yet.</EmptyState>;
  return (
    <TableScroll>
      <Table flat aria-label={`Mapping version ${String(version.version)}`}>
        <TableHeader>
          <Column isRowHeader>Row</Column>
          <Column>Wodi counts</Column>
          <Column>Sex</Column>
          <Column>Age</Column>
          <Column>Data element</Column>
          <Column>Category option combo</Column>
        </TableHeader>
        <TableBody items={version.rows}>
          {(r) => (
            <Row id={r.id}>
              <Cell>{r.label}</Cell>
              <Cell>{sourceTitle(r.sourceKey)}</Cell>
              <Cell>{sexWords(r.sexCode)}</Cell>
              <Cell>{ageWords(r)}</Cell>
              <Cell>
                <span className={styles.uid}>{r.dataElementUid}</span>
              </Cell>
              <Cell>
                <span className={styles.uid}>{r.categoryOptionComboUid}</span>
              </Cell>
            </Row>
          )}
        </TableBody>
      </Table>
    </TableScroll>
  );
}

function OrgUnit({ view }: { view: KhisMappingView }) {
  const queryClient = useQueryClient();
  const online = useOnline();
  const [uid, setUid] = useState(view.facility.orgUnitUid ?? '');
  const save = useMutation({
    mutationFn: () => setKhisOrgUnit(uid.trim()),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: khisKeys.all });
      toasts.show({ title: 'Organisation unit saved' });
    },
  });
  return (
    <Section
      title={`${view.facility.name} in KHIS`}
      description="The organisation unit KHIS lists this facility under. Copy it from KHIS."
    >
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <TextField
          label="MFL code"
          width="sm"
          value={view.facility.mflCode ?? 'Not recorded'}
          isReadOnly
        />
        <TextField label="KHIS organisation unit UID" width="md" value={uid} onChange={setUid} />
        <Button type="submit" isDisabled={!online || save.isPending || uid.trim() === ''}>
          Save organisation unit
        </Button>
      </form>
      {save.error === null ? null : (
        <Notice tone="error">{errorText(save.error, 'Not saved. Try again.')}</Notice>
      )}
    </Section>
  );
}

const BLANK_ROW: KhisMappingRowInputView = {
  label: '',
  sourceKey: 'opd_visits',
  sexCode: null,
  ageFrom: null,
  ageBelow: null,
  dataElementUid: '',
  categoryOptionComboUid: '',
};

function AgeBoundFields({
  label,
  value,
  onChange,
}: {
  label: string;
  value: { unit: AgeBoundUnit; value: number } | null;
  onChange: (next: { unit: AgeBoundUnit; value: number } | null) => void;
}) {
  return (
    <>
      <TextField
        label={label}
        description="Leave empty for no limit."
        inputMode="numeric"
        value={value === null ? '' : String(value.value)}
        onChange={(text) => {
          const n = Number(text);
          if (text.trim() === '') onChange(null);
          else if (Number.isInteger(n) && n >= 0)
            onChange({ unit: value?.unit ?? 'years', value: n });
        }}
      />
      <Select
        label={`${label}, unit`}
        options={AGE_BOUND_UNITS.map((u) => ({ id: u, label: UNIT_WORDS[u] }))}
        value={value?.unit ?? NO_LIMIT}
        isDisabled={value === null}
        onChange={(key) => {
          if (value !== null && key !== null)
            onChange({ ...value, unit: String(key) as AgeBoundUnit });
        }}
      />
    </>
  );
}

function DraftEditor({ draft, view }: { draft: KhisMappingVersionView; view: KhisMappingView }) {
  const queryClient = useQueryClient();
  const online = useOnline();
  const [dataSetUid, setDataSetUid] = useState(draft.dataSetUid ?? '');
  const [rows, setRows] = useState<KhisMappingRowInputView[]>(
    draft.rows.map((r) => ({
      label: r.label,
      sourceKey: r.sourceKey,
      sexCode: r.sexCode,
      ageFrom: r.ageFrom,
      ageBelow: r.ageBelow,
      dataElementUid: r.dataElementUid,
      categoryOptionComboUid: r.categoryOptionComboUid,
    })),
  );
  const change = (i: number, patch: Partial<KhisMappingRowInputView>) => {
    setRows((current) => current.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  };
  const refresh = () => queryClient.invalidateQueries({ queryKey: khisKeys.all });
  const save = useMutation({
    mutationFn: () =>
      saveKhisDraft(draft.id, {
        dataSetUid: dataSetUid.trim() === '' ? null : dataSetUid.trim(),
        note: null,
        rows,
      }),
    onSuccess: async () => {
      await refresh();
      toasts.show({ title: 'Draft saved' });
    },
  });
  const activate = useMutation({
    mutationFn: async () => {
      await saveKhisDraft(draft.id, {
        dataSetUid: dataSetUid.trim() === '' ? null : dataSetUid.trim(),
        note: null,
        rows,
      });
      return activateKhisDraft(draft.id);
    },
    onSuccess: async () => {
      await refresh();
      toasts.show({ title: 'Mapping activated' });
    },
  });
  const busy = save.isPending || activate.isPending;
  const error = save.error ?? activate.error;
  return (
    <Section
      title={`Draft, version ${String(draft.version)}`}
      description="Edit the rows, save the draft, then activate it. Months prepared after that use it."
    >
      <TextField label="KHIS data set UID" width="md" value={dataSetUid} onChange={setDataSetUid} />
      {rows.map((r, i) => (
        <fieldset key={i} className={styles.rowFields} aria-label={`Row ${String(i + 1)}`}>
          <p className={styles.rowHeading}>
            {`Row ${String(i + 1)}`}
            <Button
              dense
              variant="quiet"
              onPress={() => {
                setRows((current) => current.filter((_, j) => j !== i));
              }}
            >
              Remove row
            </Button>
          </p>
          <TextField
            label="Label"
            value={r.label}
            onChange={(v) => {
              change(i, { label: v });
            }}
          />
          <Select
            label="Wodi counts"
            options={view.sources.map((s) => ({ id: s.key, label: s.title }))}
            value={r.sourceKey}
            onChange={(key) => {
              const found = view.sources.find((s) => s.key === key);
              if (found !== undefined) change(i, { sourceKey: found.key });
            }}
          />
          <Select
            label="Sex"
            options={[
              { id: EVERY_SEX, label: 'Every sex' },
              ...view.sexOptions.map((o) => ({ id: o.code, label: o.display })),
            ]}
            value={r.sexCode ?? EVERY_SEX}
            onChange={(key) => {
              change(i, { sexCode: key === null || key === EVERY_SEX ? null : String(key) });
            }}
          />
          <AgeBoundFields
            label="Age from"
            value={r.ageFrom}
            onChange={(v) => {
              change(i, { ageFrom: v });
            }}
          />
          <AgeBoundFields
            label="Age under"
            value={r.ageBelow}
            onChange={(v) => {
              change(i, { ageBelow: v });
            }}
          />
          <TextField
            label="Data element UID"
            value={r.dataElementUid}
            onChange={(v) => {
              change(i, { dataElementUid: v.trim() });
            }}
          />
          <TextField
            label="Category option combo UID"
            value={r.categoryOptionComboUid}
            onChange={(v) => {
              change(i, { categoryOptionComboUid: v.trim() });
            }}
          />
        </fieldset>
      ))}
      <div className={styles.actions}>
        <Button
          onPress={() => {
            setRows((current) => [...current, BLANK_ROW]);
          }}
        >
          Add row
        </Button>
        <Button
          isDisabled={!online || busy}
          onPress={() => {
            save.mutate();
          }}
        >
          Save draft
        </Button>
        <Button
          variant="primary"
          isDisabled={!online || busy}
          onPress={() => {
            activate.mutate();
          }}
        >
          Activate this version
        </Button>
      </div>
      {error === null ? null : (
        <Notice tone="error">{errorText(error, 'Not saved. Try again.')}</Notice>
      )}
    </Section>
  );
}

function Mapping({ view }: { view: KhisMappingView }) {
  const queryClient = useQueryClient();
  const online = useOnline();
  const start = useMutation({
    mutationFn: createKhisDraft,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: khisKeys.mapping }),
  });
  return (
    <>
      <Section
        title="Active mapping"
        description="What Wodi counts for each KHIS data element and category option combo."
        actions={
          view.draft === null ? (
            <Button
              isDisabled={!online || start.isPending}
              onPress={() => {
                start.mutate();
              }}
            >
              {view.active === null ? 'Start a mapping' : 'Edit as a new draft'}
            </Button>
          ) : null
        }
      >
        {view.active === null ? (
          <EmptyState>
            No mapping is active, so no month can be prepared. Start one with the data set, data
            element and category option combo UIDs from KHIS.
          </EmptyState>
        ) : (
          <>
            <p className={styles.meta}>
              {`Version ${String(view.active.version)}, data set `}
              <span className={styles.uid}>{view.active.dataSetUid ?? ''}</span>
              {view.active.activatedAt === null
                ? ''
                : `, activated ${whenWords(view.active.activatedAt)}${view.active.activatedBy === null ? '' : ` by ${view.active.activatedBy.displayName}`}`}
            </p>
            <MappingTable version={view.active} view={view} />
          </>
        )}
        {start.error === null ? null : (
          <Notice tone="error">{errorText(start.error, 'No draft was made. Try again.')}</Notice>
        )}
      </Section>
      {view.draft === null ? null : (
        <DraftEditor key={view.draft.id} draft={view.draft} view={view} />
      )}
    </>
  );
}

export function KhisSettingsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const allowed = session !== null && can(session, 'khis.mapping.manage');
  const mapping = useQuery({ ...khisMappingQuery, enabled: allowed });
  const online = useOnline();
  return (
    <SettingsPage title="KHIS">
      <div className={screen.page}>
        {!allowed ? (
          <Notice tone="info">
            You don&apos;t have permission to change the KHIS set-up. Ask an administrator if you
            need it.
          </Notice>
        ) : null}
        {online ? null : (
          <Notice tone="info">
            You&apos;re offline. This shows what was last loaded; saving needs the connection.
          </Notice>
        )}
        {allowed && mapping.isPending ? (
          <LoadingBlock label="Loading the KHIS set-up" rows={4} />
        ) : null}
        {mapping.isError ? (
          <LoadFailed
            error={mapping.error}
            what="The KHIS set-up"
            retry={() => void mapping.refetch()}
          />
        ) : null}
        {mapping.isSuccess ? (
          <>
            <OrgUnit view={mapping.data} />
            <Mapping view={mapping.data} />
          </>
        ) : null}
      </div>
    </SettingsPage>
  );
}
